/**
 * Объёмные фигуры — учебная проекция (контур, без заливки).
 * Координаты привязаны к w и h отдельно — фигуру можно растягивать по осям.
 */

import type { GeomKind } from "@bezpart-board/shared";
import type { PathPoint } from "../pathGeometry";

export type GeomDrawLayer = {
  d: string;
  paint: "fill" | "stroke" | "dashed";
};

const VOLUME_KINDS = new Set<GeomKind>([
  "sphere",
  "frustum",
  "cone",
  "cylinder",
  "pyramid",
  "pyramid_tri",
  "box",
  "oblique_box",
]);

export function isVolumeGeomKind(kind: string): kind is GeomKind {
  return VOLUME_KINDS.has(kind as GeomKind);
}

type P = [number, number];
type Seg = [P, P];

function r1(v: number): number {
  return Math.round(v * 10) / 10;
}

function lineSeg(a: P, b: P): string {
  return `M ${r1(a[0])} ${r1(a[1])} L ${r1(b[0])} ${r1(b[1])}`;
}

function linesFrom(pairs: Seg[]): string {
  return pairs.map(([a, b]) => lineSeg(a, b)).join(" ");
}

function pushSeg(out: PathPoint[][], a: P, b: P): void {
  out.push([
    [a[0], a[1]],
    [b[0], b[1]],
  ]);
}

function pushPairs(out: PathPoint[][], pairs: Seg[]): void {
  for (const [a, b] of pairs) pushSeg(out, a, b);
}

/** Нижняя (lower=true) или верхняя (lower=false) дуга горизонтального эллипса. */
function pushEllipseArc(
  out: PathPoint[][],
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  lower: boolean
): void {
  const steps = 20;
  const pts: PathPoint[] = [];
  if (lower) {
    for (let i = 0; i <= steps; i++) {
      const t = (i / steps) * Math.PI;
      pts.push([cx - rx * Math.cos(t), cy + ry * Math.sin(t)]);
    }
  } else {
    for (let i = 0; i <= steps; i++) {
      const t = Math.PI + (i / steps) * Math.PI;
      pts.push([cx - rx * Math.cos(t), cy + ry * Math.sin(t)]);
    }
  }
  for (let i = 1; i < pts.length; i++) {
    pushSeg(out, pts[i - 1]!, pts[i]!);
  }
}

function pushFullEllipse(out: PathPoint[][], cx: number, cy: number, rx: number, ry: number): void {
  pushEllipseArc(out, cx, cy, rx, ry, true);
  pushEllipseArc(out, cx, cy, rx, ry, false);
}

/** Горизонтальный эллипс: нижняя дуга — видимая, верхняя — пунктир. */
function ellipseArcs(cx: number, cy: number, rx: number, ry: number) {
  const lx = cx - rx;
  const rx2 = cx + rx;
  return {
    full:
      `M ${r1(lx)} ${r1(cy)}` +
      ` A ${r1(rx)} ${r1(ry)} 0 1 1 ${r1(rx2)} ${r1(cy)}` +
      ` A ${r1(rx)} ${r1(ry)} 0 1 1 ${r1(lx)} ${r1(cy)}`,
    front: `M ${r1(lx)} ${r1(cy)} A ${r1(rx)} ${r1(ry)} 0 0 0 ${r1(rx2)} ${r1(cy)}`,
    back: `M ${r1(lx)} ${r1(cy)} A ${r1(rx)} ${r1(ry)} 0 0 1 ${r1(rx2)} ${r1(cy)}`,
  };
}

function wireframe(solid: string, dashed: string): GeomDrawLayer[] {
  return [
    { d: "", paint: "fill" },
    { d: solid, paint: "stroke" },
    { d: dashed, paint: "dashed" },
  ];
}

function sphereLayers(w: number, h: number): GeomDrawLayer[] {
  const cx = w / 2;
  const cy = h / 2;
  const rx = w * 0.44;
  const ry = h * 0.44;
  const eqRy = h * 0.145;

  const outline = ellipseArcs(cx, cy, rx, ry).full;
  const equator = ellipseArcs(cx, cy, rx, eqRy);

  return wireframe(`${outline} ${equator.front}`, equator.back);
}

function frustumLayers(w: number, h: number): GeomDrawLayer[] {
  const cx = w / 2;
  const topY = h * 0.18;
  const botY = h * 0.82;
  const botRx = w * 0.44;
  const topRx = w * 0.31;
  const botRy = h * 0.05;
  const topRy = h * 0.035;

  const top = ellipseArcs(cx, topY, topRx, topRy);
  const bot = ellipseArcs(cx, botY, botRx, botRy);

  const tl: P = [cx - topRx, topY];
  const tr: P = [cx + topRx, topY];
  const bl: P = [cx - botRx, botY];
  const br: P = [cx + botRx, botY];

  return wireframe(
    [top.full, lineSeg(tl, bl), lineSeg(tr, br), bot.front].join(" "),
    bot.back
  );
}

function coneLayers(w: number, h: number): GeomDrawLayer[] {
  const cx = w / 2;
  const apexY = h * 0.08;
  const baseY = h * 0.88;
  const rx = w * 0.44;
  const ry = h * 0.036;

  const apex: P = [cx, apexY];
  const base = ellipseArcs(cx, baseY, rx, ry);
  const left: P = [cx - rx, baseY];
  const right: P = [cx + rx, baseY];

  return wireframe(
    [lineSeg(apex, left), lineSeg(apex, right), base.front].join(" "),
    base.back
  );
}

function cylinderLayers(w: number, h: number): GeomDrawLayer[] {
  const cx = w / 2;
  const topY = h * 0.14;
  const botY = h * 0.86;
  const rx = w * 0.44;
  const ry = h * 0.05;

  const top = ellipseArcs(cx, topY, rx, ry);
  const bot = ellipseArcs(cx, botY, rx, ry);
  const leftTop: P = [cx - rx, topY];
  const rightTop: P = [cx + rx, topY];
  const leftBot: P = [cx - rx, botY];
  const rightBot: P = [cx + rx, botY];

  return wireframe(
    [top.full, lineSeg(leftTop, leftBot), lineSeg(rightTop, rightBot), bot.front].join(" "),
    bot.back
  );
}

/** Высота «тела» — примерно как ширина основания (не игла при высоком bbox). */
function volumeBodyHeight(w: number, h: number, ratio = 0.88): number {
  const baseSpan = w * 0.76;
  return Math.min(h * 0.58, baseSpan * ratio);
}

function pyramidLayers(w: number, h: number): GeomDrawLayer[] {
  const cx = w / 2;
  const mx = w * 0.12;
  const fw = w - mx * 2;
  const ox = fw * 0.28;
  const oy = fw * 0.16;
  const yBot = h * 0.8;
  const yTop = Math.max(h * 0.14, yBot - volumeBodyHeight(w, h));

  const a: P = [mx, yBot];
  const b: P = [mx + fw, yBot];
  const c: P = [mx + ox, yBot - oy];
  const d: P = [mx + fw + ox, yBot - oy];
  const apex: P = [cx, yTop];

  const solid: Seg[] = [
    [apex, a],
    [apex, b],
    [apex, d],
    [a, b],
    [b, d],
  ];
  const dashed: Seg[] = [
    [a, c],
    [c, d],
    [apex, c],
  ];

  return wireframe(linesFrom(solid), linesFrom(dashed));
}

/** Треугольная пирамида (тетраэдр). */
function pyramidTriLayers(w: number, h: number): GeomDrawLayer[] {
  const cx = w / 2;
  const mx = w * 0.12;
  const fw = w - mx * 2;
  const yBot = h * 0.8;
  const yTop = Math.max(h * 0.14, yBot - volumeBodyHeight(w, h, 0.78));
  const yBack = yBot - fw * 0.14;

  const front: P = [cx, yBot];
  const left: P = [mx, yBack];
  const right: P = [mx + fw, yBack];
  const apex: P = [cx, yTop];

  const solid: Seg[] = [
    [apex, front],
    [apex, left],
    [apex, right],
    [front, left],
    [front, right],
  ];
  const dashed: Seg[] = [[left, right]];

  return wireframe(linesFrom(solid), linesFrom(dashed));
}

function boxLayers(w: number, h: number): GeomDrawLayer[] {
  const ox = w * 0.2;
  const oy = h * 0.11;
  const padL = w * 0.1;
  const padR = w * 0.08;
  const padB = h * 0.06;
  const padT = h * 0.06;

  const yBot = h - padB;
  const yTop = padT + oy;
  const fw = w - padL - padR - ox;
  const x0 = padL;

  const a: P = [x0, yBot];
  const b: P = [x0 + fw, yBot];
  const c: P = [x0, yTop];
  const d: P = [x0 + fw, yTop];
  const e: P = [x0 + ox, yBot - oy];
  const f: P = [x0 + fw + ox, yBot - oy];
  const hh: P = [x0 + ox, yTop - oy];
  const k: P = [x0 + fw + ox, yTop - oy];

  const solid: [P, P][] = [
    [a, b],
    [b, d],
    [d, c],
    [c, a],
    [b, f],
    [f, k],
    [k, d],
    [c, hh],
    [hh, k],
  ];
  const dashed: [P, P][] = [
    [a, e],
    [e, f],
    [e, hh],
  ];

  return wireframe(linesFrom(solid), linesFrom(dashed));
}

/** Наклонный параллелепипед: верхний контур сдвинут вправо, рёбра наклонены. */
function obliqueBoxLayers(w: number, h: number): GeomDrawLayer[] {
  const padL = w * 0.08;
  const padR = w * 0.06;
  const padB = h * 0.06;
  const padT = h * 0.06;

  const ox = w * 0.15;
  const oy = h * 0.09;
  const shear = w * 0.12;
  const fw = w - padL - padR - ox - shear;

  const yBot = h - padB;
  const yTop = padT;
  const x0 = padL;

  const a: P = [x0, yBot];
  const b: P = [x0 + fw, yBot];
  const c: P = [x0 + ox, yBot - oy];
  const d: P = [x0 + fw + ox, yBot - oy];

  const ap: P = [x0 + shear, yTop];
  const bp: P = [x0 + fw + shear, yTop];
  const cp: P = [x0 + ox + shear, yTop - oy];
  const dp: P = [x0 + fw + ox + shear, yTop - oy];

  const solid: [P, P][] = [
    [a, b],
    [b, d],
    [ap, bp],
    [bp, dp],
    [dp, cp],
    [cp, ap],
    [a, ap],
    [b, bp],
    [d, dp],
  ];
  const dashed: [P, P][] = [
    [d, c],
    [c, a],
    [c, cp],
  ];

  return wireframe(linesFrom(solid), linesFrom(dashed));
}

export function geomVolumeLayers(kind: GeomKind, w: number, h: number): GeomDrawLayer[] | null {
  if (!isVolumeGeomKind(kind)) return null;
  switch (kind) {
    case "sphere":
      return sphereLayers(w, h);
    case "frustum":
      return frustumLayers(w, h);
    case "cone":
      return coneLayers(w, h);
    case "cylinder":
      return cylinderLayers(w, h);
    case "pyramid":
      return pyramidLayers(w, h);
    case "pyramid_tri":
      return pyramidTriLayers(w, h);
    case "box":
      return boxLayers(w, h);
    case "oblique_box":
      return obliqueBoxLayers(w, h);
    default:
      return null;
  }
}

export function geomVolumeCombinedPathD(kind: GeomKind, w: number, h: number): string | null {
  const layers = geomVolumeLayers(kind, w, h);
  if (!layers) return null;
  return layers
    .filter((l) => l.paint === "fill" || l.paint === "stroke")
    .map((l) => l.d)
    .join(" ");
}

/** Полилинии контура для hit-test / marquee (локальные координаты 0…w, 0…h). */
export function volumeShapePolylines(kind: GeomKind, w: number, h: number): PathPoint[][] {
  const out: PathPoint[][] = [];
  if (!isVolumeGeomKind(kind)) return out;

  const cx = w / 2;

  if (kind === "sphere") {
    pushFullEllipse(out, cx, h / 2, w * 0.44, h * 0.44);
    pushEllipseArc(out, cx, h / 2, w * 0.44, h * 0.145, true);
    pushEllipseArc(out, cx, h / 2, w * 0.44, h * 0.145, false);
    return out;
  }

  if (kind === "frustum") {
    const topY = h * 0.18;
    const botY = h * 0.82;
    const botRx = w * 0.44;
    const topRx = w * 0.31;
    pushFullEllipse(out, cx, topY, topRx, h * 0.035);
    pushSeg(out, [cx - topRx, topY], [cx - botRx, botY]);
    pushSeg(out, [cx + topRx, topY], [cx + botRx, botY]);
    pushEllipseArc(out, cx, botY, botRx, h * 0.05, true);
    pushEllipseArc(out, cx, botY, botRx, h * 0.05, false);
    return out;
  }

  if (kind === "cone") {
    const apexY = h * 0.08;
    const baseY = h * 0.88;
    const rx = w * 0.44;
    const apex: P = [cx, apexY];
    pushSeg(out, apex, [cx - rx, baseY]);
    pushSeg(out, apex, [cx + rx, baseY]);
    pushEllipseArc(out, cx, baseY, rx, h * 0.036, true);
    pushEllipseArc(out, cx, baseY, rx, h * 0.036, false);
    return out;
  }

  if (kind === "cylinder") {
    const topY = h * 0.14;
    const botY = h * 0.86;
    const rx = w * 0.44;
    const ry = h * 0.05;
    pushFullEllipse(out, cx, topY, rx, ry);
    pushSeg(out, [cx - rx, topY], [cx - rx, botY]);
    pushSeg(out, [cx + rx, topY], [cx + rx, botY]);
    pushEllipseArc(out, cx, botY, rx, ry, true);
    pushEllipseArc(out, cx, botY, rx, ry, false);
    return out;
  }

  if (kind === "pyramid") {
    const mx = w * 0.12;
    const fw = w - mx * 2;
    const ox = fw * 0.28;
    const oy = fw * 0.16;
    const yBot = h * 0.8;
    const yTop = Math.max(h * 0.14, yBot - volumeBodyHeight(w, h));
    const cx = w / 2;
    const a: P = [mx, yBot];
    const b: P = [mx + fw, yBot];
    const c: P = [mx + ox, yBot - oy];
    const d: P = [mx + fw + ox, yBot - oy];
    const apex: P = [cx, yTop];
    pushPairs(out, [
      [apex, a],
      [apex, b],
      [apex, d],
      [a, b],
      [b, d],
      [a, c],
      [c, d],
      [apex, c],
    ]);
    return out;
  }

  if (kind === "pyramid_tri") {
    const mx = w * 0.12;
    const fw = w - mx * 2;
    const yBot = h * 0.8;
    const yTop = Math.max(h * 0.14, yBot - volumeBodyHeight(w, h, 0.78));
    const yBack = yBot - fw * 0.14;
    const cx = w / 2;
    const front: P = [cx, yBot];
    const left: P = [mx, yBack];
    const right: P = [mx + fw, yBack];
    const apex: P = [cx, yTop];
    pushPairs(out, [
      [apex, front],
      [apex, left],
      [apex, right],
      [front, left],
      [front, right],
      [left, right],
    ]);
    return out;
  }

  if (kind === "box") {
    const ox = w * 0.2;
    const oy = h * 0.11;
    const padL = w * 0.1;
    const padR = w * 0.08;
    const padB = h * 0.06;
    const padT = h * 0.06;
    const yBot = h - padB;
    const yTop = padT + oy;
    const fw = w - padL - padR - ox;
    const x0 = padL;
    const a: P = [x0, yBot];
    const b: P = [x0 + fw, yBot];
    const c: P = [x0, yTop];
    const d: P = [x0 + fw, yTop];
    const e: P = [x0 + ox, yBot - oy];
    const f: P = [x0 + fw + ox, yBot - oy];
    const hh: P = [x0 + ox, yTop - oy];
    const k: P = [x0 + fw + ox, yTop - oy];
    pushPairs(out, [
      [a, b], [b, d], [d, c], [c, a],
      [b, f], [f, k], [k, d], [c, hh], [hh, k],
      [a, e], [e, f], [e, hh],
    ]);
    return out;
  }

  if (kind === "oblique_box") {
    const padL = w * 0.08;
    const padR = w * 0.06;
    const padB = h * 0.06;
    const padT = h * 0.06;
    const ox = w * 0.15;
    const oy = h * 0.09;
    const shear = w * 0.12;
    const fw = w - padL - padR - ox - shear;
    const yBot = h - padB;
    const yTop = padT;
    const x0 = padL;
    const a: P = [x0, yBot];
    const b: P = [x0 + fw, yBot];
    const c: P = [x0 + ox, yBot - oy];
    const d: P = [x0 + fw + ox, yBot - oy];
    const ap: P = [x0 + shear, yTop];
    const bp: P = [x0 + fw + shear, yTop];
    const cp: P = [x0 + ox + shear, yTop - oy];
    const dp: P = [x0 + fw + ox + shear, yTop - oy];
    pushPairs(out, [
      [a, b], [b, d], [ap, bp], [bp, dp], [dp, cp], [cp, ap],
      [a, ap], [b, bp], [d, dp],
      [d, c], [c, a], [c, cp],
    ]);
  }

  return out;
}
